import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import SkillsAndHobbiesModal from 'components/modals/addTagsModal/SkillsAndHobbiesModal';
import { SEARCH } from 'constants/routes';
import { UserType } from 'hooks/useUserData';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Edit2LineIcon from 'remixicon-react/Edit2LineIcon';
import ArrowDownSFillIcon from 'remixicon-react/ArrowDownSFillIcon';
import { ReactComponent as SMEBadge } from 'assets/icons/sme-badge.svg';

export type SkillCategoryType =
  | 'skills'
  | 'hobbies'
  | 'smeSkills'
  | 'projectTypes'
  | 'buildingSystems'
  | 'professionalExperiences';
type SkillsProps = {
  isLoggedInUser: boolean;
  user: UserType;
  canModifySkills: boolean;
  type: SkillCategoryType;
  title: string;
  skills?: string[];
};

type TitleProps = TypographyProps & {
  colorVariant?: string;
};

const Skills = ({ isLoggedInUser, user, canModifySkills, type, title, skills }: SkillsProps) => {
  const theme = useTheme();
  const [openSkillsModal, setOpenSkillsModal] = useState(false);
  const { state } = useLocation();
  const [expanded, setExpanded] = useState<boolean>(true);
  const [loadAll, setLoadAll] = useState(false);
  const chipLimit = 3;

  const handleAccordionChange = () => {
    setExpanded((prevExpanded) => !prevExpanded);
    setLoadAll(false);
  };

  useEffect(() => {
    if (!!skills?.length) {
      setExpanded(true);
    }
  }, [skills]);

  const ChipComponent = ({ skill, index = 0, loadMore = false }) => {
    return (
      <Chip
        {...(!loadMore && {
          component: Link,
          to: `${SEARCH}?q=${skill}`,
          state: state,
        })}
        onClick={loadMore ? () => setLoadAll(true) : undefined}
        key={`${index}-${skill}`}
        label={skill}
        variant="outlined"
        sx={(theme) => ({
          mr: 1,
          mb: 1,
          p: 2,
          cursor: 'pointer',
          ...(type === 'smeSkills' &&
            !loadMore && {
              color: theme.palette.custom.link,
              backgroundColor: theme.palette.custom.aliceBlue,
              border: 'none',
            }),
        })}
      />
    );
  };
  return (
    <>
      {skills?.length ? (
        <Accordion
          sx={{
            my: 1,
            boxShadow: 'none',
            mb: 2,
            borderRadius: '8px',
            py: '4px',
            '&::before': { display: 'none' },
          }}
          expanded={expanded}
          onChange={handleAccordionChange}
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={<ArrowDownSFillIcon />}
            sx={{ py: canModifySkills ? 0 : 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {type === 'smeSkills' && (
                <SMEBadge style={{ width: 24, height: 24, marginRight: 4 }} />
              )}
              <Title colorVariant={type === 'smeSkills' ? 'link' : 'tuna'}>{title}</Title>
            </Box>
            {canModifySkills && (
              <IconButton
                onClick={() => {
                  setOpenSkillsModal(true);
                }}
                sx={{ marginLeft: 'auto' }}
              >
                <Edit2LineIcon color={theme.palette.custom.lightBlack} />
              </IconButton>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ mt: -2 }}>
            <Stack direction="row" flexWrap="wrap" my={1}>
              {!!skills?.length &&
                (loadAll ? skills : skills.slice(0, chipLimit)).map((skill, index) => (
                  <ChipComponent skill={skill} index={index} />
                ))}
              {!loadAll && skills?.length > chipLimit && (
                <ChipComponent skill={`+${skills.slice(chipLimit).length} More`} loadMore />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : (
        <Container sx={{ padding: canModifySkills ? '16px' : '24px 16px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {type === 'smeSkills' && (
                <SMEBadge style={{ width: 24, height: 24, marginRight: 4 }} />
              )}
              <Title colorVariant={type === 'smeSkills' ? 'link' : 'tuna'}>{title}</Title>
            </Box>
            {canModifySkills && (
              <IconButton onClick={() => setOpenSkillsModal(true)} sx={{ marginLeft: 'auto' }}>
                <Edit2LineIcon color={theme.palette.custom.lightBlack} />
              </IconButton>
            )}
          </Box>
        </Container>
      )}
      {openSkillsModal && (
        <SkillsAndHobbiesModal
          type={type}
          open={openSkillsModal}
          setOpen={setOpenSkillsModal}
          isLoggedInUser={isLoggedInUser}
          userDetails={user}
        />
      )}
    </>
  );
};

const Container = styled(Paper, { label: 'Container' })(({ theme }) => ({
  width: '100%',
  background: theme.palette.custom.white,
  borderRadius: 8,
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  marginBottom: '16px',
}));

const Title = styled(Typography, { label: 'Title' })<TitleProps>(({ theme, colorVariant }) => ({
  fontWeight: 700,
  lineHeight: '19px',
  letterSpacing: '0.25px',
  fontSize: 16,
  color: colorVariant ? theme.palette.custom[colorVariant] : theme.palette.custom.tuna,
}));

const Note = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.custom.mistBlue,
  margin: '8px 0 12px',
}));

export default Skills;
